/**
 * Card Video Autoplay Handler
 * Handles automatic playback of videos in product cards
 */

(function() {
  'use strict';

  // Configuration
  const config = {
    autoplaySelector: '.card-product-video',
    cardSelector: '.card-product',
    observerOptions: {
      root: null,
      rootMargin: '50px',
      threshold: 0.1
    }
  };

  /**
   * Try to play video with error handling
   */
  function tryPlayVideo(video) {
    if (!video || video.paused === undefined) return;
    
    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise
        .catch((error) => {
          console.log('Video autoplay prevented:', error.message);
        });
    }
  }

  /**
   * Initialize Intersection Observer for video autoplay
   */
  function initIntersectionObserver() {
    // Check if IntersectionObserver is supported
    if (!('IntersectionObserver' in window)) {
      console.warn('IntersectionObserver not supported, using fallback');
      // Fallback: play all videos
      document.querySelectorAll(config.autoplaySelector).forEach((video) => {
        tryPlayVideo(video);
      });
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        
        if (entry.isIntersecting) {
          // Video is in viewport
          tryPlayVideo(video);
        } else {
          // Video is out of viewport
          if (video && !video.paused) {
            video.pause();
          }
        }
      });
    }, config.observerOptions);

    // Observe all videos in cards
    document.querySelectorAll(config.autoplaySelector).forEach((video) => {
      observer.observe(video);
    });
  }

  /**
   * Handle video pause on card interaction
   */
  function handleCardInteraction() {
    document.querySelectorAll(config.cardSelector).forEach((card) => {
      const video = card.querySelector(config.autoplaySelector);
      
      if (!video) return;

      // Store original autoplay state
      video.dataset.autoplay = true;

      // Pause video when hovering over the card
      card.addEventListener('mouseenter', () => {
        if (video && !video.paused) {
          video.pause();
        }
      });

      // Resume video when leaving the card
      card.addEventListener('mouseleave', () => {
        if (video && video.paused && !video.ended && video.dataset.autoplay) {
          tryPlayVideo(video);
        }
      });
    });
  }

  /**
   * Set up video attributes for smooth playback
   */
  function configureVideoAttributes() {
    document.querySelectorAll(config.autoplaySelector).forEach((video) => {
      // Ensure video has required attributes
      video.setAttribute('playsinline', '');
      video.setAttribute('muted', '');
      video.setAttribute('loop', '');
      video.setAttribute('autoplay', '');
      
      // Ensure there's a preload attribute
      if (!video.getAttribute('preload')) {
        video.setAttribute('preload', 'metadata');
      }

      // Add data attributes for tracking
      if (!video.dataset.initialized) {
        video.dataset.initialized = true;
        
        // Listen for video load errors
        video.addEventListener('error', function(e) {
          console.error('Video load error:', e);
          // Show preview image on error
          const card = video.closest('.card-product');
          if (card) {
            const preview = card.querySelector('img');
            if (preview) {
              preview.style.display = 'block';
            }
          }
        });

        // Try to play on canplay event
        video.addEventListener('canplay', function() {
          if (isVideoInViewport(this)) {
            tryPlayVideo(this);
          }
        });
      }
    });
  }

  /**
   * Check if video is in viewport
   */
  function isVideoInViewport(video) {
    const rect = video.getBoundingClientRect();
    return (
      rect.top < window.innerHeight &&
      rect.bottom > 0
    );
  }

  /**
   * Initialize all video autoplay features
   */
  function init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        configureVideoAttributes();
        initIntersectionObserver();
        handleCardInteraction();
      });
    } else {
      configureVideoAttributes();
      initIntersectionObserver();
      handleCardInteraction();
    }

    // Re-initialize on dynamic content load (AJAX/infinite scroll)
    if (window.MutationObserver) {
      const observer = new MutationObserver((mutations) => {
        let hasNewVideos = false;
        mutations.forEach((mutation) => {
          if (mutation.addedNodes.length) {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === 1) { // Element node
                if (node.classList && (node.classList.contains('card-product') || 
                    node.querySelector(config.autoplaySelector))) {
                  hasNewVideos = true;
                }
              }
            });
          }
        });
        
        if (hasNewVideos) {
          configureVideoAttributes();
          initIntersectionObserver();
          handleCardInteraction();
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }

  // Initialize when script loads
  init();
})();
